import { useEffect, useRef } from "react";

import {
  USER_UPDATED_KEY,
  fetchUserById,
  parseUserUpdatedValue,
} from "../helpers/userEditTab";

// A focus refetch is the belt-and-braces path for when the `storage` event
// never arrives (storage disabled, or the tab was opened before the listener
// mounted). Bursts of focus events must not turn into bursts of GETs.
const FOCUS_THROTTLE_MS = 3000;

/**
 * Keep one selected user's snapshot fresh while it is edited in another tab.
 *
 * `onUpdated` receives the re-read user from GET /api/users/{id} — callers
 * decide how to merge it, so nothing here can widen a deliberate
 * relation-preserving merge (see useRequest.setRequestPreservingRelations).
 *
 * `refreshOnFocus` is opt-in: it is right for the ONE selected user a section
 * displays, and wrong for a search-results list, where it would fire a request
 * per row on every focus.
 */
const useUserUpdates = (userId, onUpdated, { refreshOnFocus = false } = {}) => {
  const handlerRef = useRef(onUpdated);
  const lastFocusFetchRef = useRef(0);

  useEffect(() => {
    handlerRef.current = onUpdated;
  }, [onUpdated]);

  useEffect(() => {
    if (!userId) return undefined;

    let cancelled = false;
    const id = Number(userId);

    const reload = async () => {
      try {
        const user = await fetchUserById(id);
        if (!cancelled && user) handlerRef.current?.(user);
      } catch (e) {
        // A failed refresh leaves the existing snapshot on screen, which is
        // strictly better than blanking the assigned user.
      }
    };

    const onStorage = (e) => {
      if (e.key !== USER_UPDATED_KEY) return;
      if (parseUserUpdatedValue(e.newValue) !== id) return;
      reload();
    };

    const onFocus = () => {
      const now = Date.now();
      if (now - lastFocusFetchRef.current < FOCUS_THROTTLE_MS) return;
      lastFocusFetchRef.current = now;
      reload();
    };

    window.addEventListener("storage", onStorage);
    if (refreshOnFocus) window.addEventListener("focus", onFocus);

    return () => {
      cancelled = true;
      window.removeEventListener("storage", onStorage);
      if (refreshOnFocus) window.removeEventListener("focus", onFocus);
    };
  }, [userId, refreshOnFocus]);
};

export default useUserUpdates;
